// LoginPage.js
import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import loginImage from '../../../assets/login-image.png';
import formCornerImage from '../../../assets/formBg.png';
import CustomPaperBox from '../../../components/common/CustomPaperBox';
import InputTextField from '../../../components/common/CustomTextInput';
import CustomButton from '../../../components/common/CustomButton/CustomButton';
import { sendMobileOtp, verifyMobileOtp } from '../../../utils/urls';
import OtpVerificationForm from './OtpVerificationForm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { setTokens, setUserData } from '../../../Redux Store/Slices/auth';
import { useDispatch } from 'react-redux';
import { errorToast, successToast } from '../../../utils/toasts';

const LoginPage = () => {
  const [otpSent, setOtpSent] = useState(false);
  const [mobileNumber, setMobileNumber] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSendOtp = async ({ mobile_number }, { setSubmitting }) => {
    try {
      const response = await axios.post(sendMobileOtp, {
        mobile_number,
        purpose_for: 'login',
      });

      const token = response.data?.data?.token;
      if (!token) throw new Error('Token not received from server');

      setOtpToken(token);
      setMobileNumber(mobile_number);
      setOtpSent(true);
      setErrorMessage('');
    } catch (err) {
      setErrorMessage(`Failed to send OTP: ${err.response?.data?.message || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (otp, setSubmitting) => {
    try {
      if (!otpToken) throw new Error('No OTP token available for verification');

      const response = await axios.post(verifyMobileOtp, {
        token: otpToken,
        otp,
        mobile_number: mobileNumber,
        purpose_for: 'login',
      });

      if (response?.data?.data?.message === 'User not found') {
        // sessionStorage.setItem('skip_login_mobile_number', mobileNumber); 
        dispatch(setUserData({
          mobile_number: mobileNumber,
          isSkipLogin: true
        }));
        navigate("/");
        setSubmitting(false);
      } else if (response.data.success === true) {
        toast.success("Login Successfully")
  
        const data = response?.data?.data;
        const tokens = response.data?.token;
  
        dispatch(setTokens(tokens));
        dispatch(setUserData(data));

        setTimeout(() => {
          navigate("/");
        }, 500);
  
        setErrorMessage('');
        setSubmitting(false);
        // navigate('/advisor-onboarding');
      } else {
        const apiError = response.data.error;
        if (Array.isArray(apiError)) {
          apiError.forEach((err) => {
            if (err.path === "password") {
              setErrorMessage(err.msg);
            } else if (err.path === "email") {
              setErrorMessage(err.msg);
            }
          });
          setSubmitting(false);
        } else {
          errorToast(response.data.error.message);
          setSubmitting(false);
        }
      }
    } catch (err) {
      setErrorMessage(`OTP verification failed: ${err.response?.data?.error?.message || err.message}`);
      setSubmitting(false);
    }
  };

  const handleResendOtp = async (resendSuccess) => {
    try {
      const response = await axios.post(sendMobileOtp, {
        mobile_number: mobileNumber,
        purpose_for: 'login',
      });

      const token = response.data?.data?.token;
      if (!token) throw new Error('Token not received from server');

      setOtpToken(token);
      successToast('OTP resent successfully');
      if (resendSuccess) {
        resendSuccess();
      }
    } catch (err) {
      setErrorMessage(`Failed to resend OTP: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2">
      <div className="hidden md:flex items-center justify-center">
        <img
          src={loginImage}
          alt="Login Visual"
          className="max-h-[50vh] w-auto object-contain"
        />
      </div>

      <div className="flex items-center justify-center p-8 bg-[#014E7C] rounded-tl-3xl rounded-bl-3xl relative">
        <CustomPaperBox className="w-full max-w-md p-7 relative" elevation="md" variant="default">
          <h2 className="text-3xl font-bold text-center text-black whitespace-pre-line">
            {otpSent
              ? 'Verify Your Account!'
              : 'Secure Your Future\nWith Life Insurance'}
          </h2>
          {otpSent && (
            <p className="text-sm text-center text-gray-500 mb-6">
              We sent a code to your phone number <br />
              +91-*******{mobileNumber.slice(-3)}
            </p>
          )}
          {errorMessage && (
            <p className="text-center text-red-500 mb-4">{errorMessage}</p>
          )}

          {otpSent ? (
            <OtpVerificationForm
              mobileNumber={mobileNumber}
              onSubmit={handleVerifyOtp}
              onResend={handleResendOtp}
            />
          ) : (
            <Formik
              initialValues={{ mobile_number: '' }}
              validationSchema={Yup.object({
                mobile_number: Yup.string()
                  .required('Mobile number is required')
                  .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits'),
              })}
              onSubmit={handleSendOtp}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-5">
                  <InputTextField
                    name="mobile_number"
                    label="Mobile Number"
                    placeholder="Enter your mobile number"
                    type="text"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    }}
                  />
                  <div className="space-y-4 w-full">
                    <CustomButton
                      type="submit"
                      label="Continue"
                      variant="contained"
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                      fullWidth={true}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        dispatch(setUserData({ isSkipLogin: true }));
                        navigate("/");
                      }}
                      className="w-full py-2.5 text-[0.875rem] font-semibold text-[#64748B] hover:text-[#0F172A] transition-colors rounded-lg hover:bg-black/5"
                    >
                      Browse as Guest
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          )}
        </CustomPaperBox>
        <img
          src={formCornerImage}
          alt="Decorative Corner"
          className="absolute bottom-0 right-0 w-200 h-200 object-contain opacity-80 pointer-events-none"
        />
      </div>
    </div>
  );
};

export default LoginPage;
